import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../elementsui/CartContext';
import './OrderPlacement.css';
import Preloader from '../common/preloader';
import payment from './payment.svg'; 
import cod from './cod.svg'; 
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const OrderPlacement = () => {
  const { cartItems, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [selectedAddress, setSelectedAddress] = useState('');
  const [address, setAddress] = useState({
    name: '',
    locality: '',
    subLocality: '',
    district: '',
    pincode: '',
    phone: '',
  });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showUPIAlert, setShowUPIAlert] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchAddresses = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSavedAddresses(response.data.addresses || []);
      } catch (error) {
        setErrorMessage('Failed to load addresses. Please try again.');
        setShowErrorPopup(true);
      } finally {
        setLoading(false);
      }
    };
    fetchAddresses();
  }, [navigate]);

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    if (method === 'upi') {
      setShowUPIAlert(true);
    } else {
      setShowUPIAlert(false);
    }
  };

  const handleAddressChange = (e) => {
    setAddress((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddressSelect = (e) => {
    const id = e.target.value;
    setSelectedAddress(id);
    if (id) {
      const sel = savedAddresses.find((addr) => addr._id === id);
      setAddress({
        name: sel.name,
        locality: sel.locality,
        subLocality: sel.subLocality || '',
        district: sel.district,
        pincode: sel.pincode,
        phone: sel.phone,
      });
    } else {
      setAddress({
        name: '',
        locality: '',
        subLocality: '',
        district: '',
        pincode: '',
        phone: '',
      });
    }
  };

  const handlePlaceOrder = async () => {
    if (paymentMethod === 'upi') {
      setShowUPIAlert(true);
      return;
    }

    if (!address.name || !address.locality || !address.district || !address.pincode || !address.phone) {
      setErrorMessage('Please fill in all required address fields.');
      setShowErrorPopup(true);
      return;
    }
    if (!address.pincode.match(/^\d{6}$/)) {
      setErrorMessage('Pincode must be 6 digits.');
      setShowErrorPopup(true);
      return;
    }
    if (!address.phone.match(/^\d{10}$/)) {
      setErrorMessage('Phone must be 10 digits.');
      setShowErrorPopup(true);
      return;
    }
    if (cartItems.length === 0) {
      setErrorMessage('Your cart is empty.');
      setShowErrorPopup(true);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const deliveryDetails = {
        name: address.name,
        mobile: address.phone,
        pincode: address.pincode,
        address: `${address.locality}${address.subLocality ? ', ' + address.subLocality : ''}`,
        city: address.district,
        state: 'Unknown',
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/orders/place`,
        { deliveryDetails, items: cartItems, paymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await clearCart();
      navigate('/order-confirmation', { state: { orderDetails: response.data } });
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to place order. Please try again.');
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const closeErrorPopup = () => {
    setShowErrorPopup(false);
    setErrorMessage('');
  };

  if (loading) {
    return <Preloader />;
  }

  return (
    <div className="op-container">
      <div className="op-section">
        <h2 className="op-section-heading">Order Summary</h2>
        <div className="op-cart-items">
          {cartItems.length === 0 ? (
            <p className="op-text-muted">Your cart is empty.</p>
          ) : (
            cartItems.map((item) => (
              <div key={`${item.productId}-${item.weight}`} className="op-cart-item">
                <img src={item.image} alt={item.name} className="op-item-image" />
                <div className="op-item-details">
                  <p className="op-item-name">{item.name}</p>
                  <p className="op-item-quantity">
                    Qty: {item.quantity}, Weight: {item.weight}
                  </p>
                  <p className="op-item-price">₹{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="op-order-total">
          <span>Total:</span>
          <span className="op-total-amount">₹{totalPrice.toFixed(2)}</span>
        </div>
      </div>

      <div className="op-section">
        <h2 className="op-section-heading">Delivery Address</h2>
        <select
          className="op-address-dropdown my-3"
          value={selectedAddress}
          onChange={handleAddressSelect}
        >
          <option value="">Select Saved Address or Add New</option>
          {savedAddresses.length > 0 ? (
            savedAddresses.map((addr) => (
              <option key={addr._id} value={addr._id}>
                {addr.name}, {addr.locality}, {addr.district}
              </option>
            ))
          ) : (
            <option disabled>No saved addresses available</option>
          )}
        </select>

        <div className="op-address-form">
          <input
            type="text"
            name="name"
            value={address.name}
            onChange={handleAddressChange}
            placeholder="Enter Your Full Name"
            className="op-form-input"
            required
          />
          <input
            type="text"
            name="locality"
            value={address.locality}
            onChange={handleAddressChange}
            placeholder="Enter Your Locality"
            className="op-form-input"
            required
          />
          <input
            type="text"
            name="subLocality"
            value={address.subLocality}
            onChange={handleAddressChange}
            placeholder="Sub-Locality (Optional)"
            className="op-form-input"
          />
          <input
            type="text"
            name="district"
            value={address.district}
            onChange={handleAddressChange}
            placeholder="Enter Your District"
            className="op-form-input"
            required
          />
          <input
            type="text"
            name="pincode"
            value={address.pincode}
            onChange={handleAddressChange}
            placeholder="Enter Pincode (6 digits)"
            className="op-form-input"
            required
          />
          <input
            type="text"
            name="phone"
            value={address.phone}
            onChange={handleAddressChange}
            placeholder="Enter Phone number (10 digits)"
            className="op-form-input"
            required
          />
        </div>
      </div>

      <div className="op-section">
        <h2 className="op-section-heading">Payment Method</h2>
        <div className="op-payment-methods">
          <label className="op-payment-card">
            <input
              type="radio"
              name="payment"
              value="cash"
              checked={paymentMethod === 'cash'}
              onChange={() => handlePaymentMethodChange('cash')}
              className="op-form-radio"
            />
            <div className="op-payment-content">
              <img src={cod} alt="Cash on Delivery" className="op-payment-icon" />
              <span>Cash on Delivery</span>
            </div>
          </label>
          <label className="op-payment-card">
            <input
              type="radio"
              name="payment"
              value="upi"
              checked={paymentMethod === 'upi'}
              onChange={() => handlePaymentMethodChange('upi')}
              className="op-form-radio"
            />
            <div className="op-payment-content">
              <img src={payment} alt="UPI" className="op-payment-icon" />
              <span>UPI App</span>
            </div>
          </label>
        </div>

        {showUPIAlert && (
          <div className="alert alert-warning mt-3 small" role="alert">
            <strong>Note:</strong> Currently, we are offering only cash on delivery. Please select cash on delivery!
          </div>
        )}

        <button
          className="op-confirm-btn mt-5"
          onClick={handlePlaceOrder}
          disabled={loading || paymentMethod === 'upi'}
        >
          Place Order
        </button>
      </div>

      {showErrorPopup && (
        <div className="op-popup-overlay">
          <div className="op-popup">
            <h2 className="op-popup-heading">Error</h2>
            <p className="op-popup-message">{errorMessage}</p>
            <div className="op-popup-buttons">
              <button
                className="op-popup-btn op-popup-ok-btn"
                onClick={closeErrorPopup}
                aria-label="Close error popup"
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderPlacement;